import React, { ReactElement, useState } from 'react';
import { Button, DatePicker, SalvDropdown, SalvNumberField, SalvTextField, Size } from '@salvapp/ui-components';
import { AlertFilterField, AlertFilterObject, AlertFilterObjects } from './helper';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import dayjs, { Dayjs } from 'dayjs';
import { InternalApiAlertFilterExpressionFieldTypeEnum } from '../../api-internal';
import { assertExhaustive } from '../../common/helper';

export interface AlertFiltersConditionProps {
  expression: AlertFiltersConditionExpressionProps;
  alertFiltersObjects: AlertFilterObjects;
  removable?: boolean;
  onConditionChange: (condition: any) => void;
  removeCondition: () => void;
}

export interface AlertFiltersConditionExpressionProps {
  target: DropdownOption;
  field: DropdownOption;
  operation: DropdownOption;
  fieldType: InternalApiAlertFilterExpressionFieldTypeEnum;
  value: any;
}

const renderValueContainer = (
  type: InternalApiAlertFilterExpressionFieldTypeEnum,
  operation: string | undefined,
  value: any,
  setFieldValue: (value: DropdownOption | DropdownOption[] | Dayjs | string | number) => void,
  loadValues: () => DropdownOption[]
): ReactElement => {
  switch (type) {
    case InternalApiAlertFilterExpressionFieldTypeEnum.NUMBER:
      switch (operation) {
        case 'IS_BETWEEN':
          return (
            <>
              <SalvNumberField
                ariaLabel='Lower bound'
                size={Size.SMALL}
                value={Array.isArray(value) ? value[0] : 0}
                onChange={(newValue: number) => setFieldValue([newValue, value && value[1] ? value[1] : 0])}
              />
              <div className='mx-3 align-self-center'>&</div>
              <SalvNumberField
                ariaLabel='Upper bound'
                size={Size.SMALL}
                value={Array.isArray(value) ? value[1] : 0}
                onChange={(newValue: number) => setFieldValue([value && value[0] ? value[0] : 0, newValue])}
              />
            </>
          );
        default:
          return (
            <SalvNumberField
              ariaLabel='Filter input'
              size={Size.SMALL}
              value={value !== undefined ? value : ''}
              onChange={(newValue: number) => setFieldValue(newValue)}
            />
          );
      }
    case InternalApiAlertFilterExpressionFieldTypeEnum.STRING:
      return (
        <SalvTextField
          ariaLabel='Filter input'
          size={Size.SMALL}
          value={value !== undefined ? value : ''}
          onChange={(newValue: string) => setFieldValue(newValue)}
        />
      );
    case InternalApiAlertFilterExpressionFieldTypeEnum.STRING_LIST:
      return (
        <SalvDropdown
          ariaLabel='Filter input'
          isMulti
          value={value}
          size={Size.SMALL}
          options={loadValues()}
          onChange={(select: DropdownOption[]) => setFieldValue(select)}
          clearable={false}
          renderedInBootstrapModal
        />
      );
    case InternalApiAlertFilterExpressionFieldTypeEnum.TIMESTAMP:
      switch (operation) {
        case 'IS_BETWEEN':
          return (
            <>
              <DatePicker
                size='small'
                value={value && value[0] ? value[0] : null}
                showDefaultOptions={false}
                showTime={true}
                onChange={(newValue: Dayjs | null) => {
                  if (newValue) {
                    setFieldValue([newValue, value && value[1] ? value[1] : undefined]);
                  }
                }}
                isClearable={false}
              />
              <div className='mx-3 align-self-center'>&</div>
              <DatePicker
                size='small'
                value={value && value[1] ? value[1] : null}
                showDefaultOptions={false}
                showTime={true}
                onChange={(newValue: Dayjs | null) => {
                  if (newValue) {
                    setFieldValue([value && value[0] ? value[0] : undefined, newValue]);
                  }
                }}
                isClearable={false}
              />
            </>
          );
        default:
          return (
            <DatePicker
              size='small'
              value={value ? value : null}
              showDefaultOptions={false}
              showTime={true}
              onChange={(value: Dayjs | null) => {
                if (value) {
                  setFieldValue(value);
                }
              }}
              isClearable={false}
            />
          );
      }
    default:
      return assertExhaustive(type);
  }
};

const validateCondition = (
  condition: Partial<AlertFiltersConditionExpressionProps>,
  alertFiltersObjects: AlertFilterObjects
): Partial<AlertFiltersConditionExpressionProps> => {
  // If current selections for target, field and operation are valid
  const target: AlertFilterObject | undefined = alertFiltersObjects.objects.find(
    (object) => object.value.key === condition.target?.key
  );
  const field: AlertFilterField | undefined = target?.fields.find((field) => field.value.key === condition.field?.key);
  const operation: DropdownOption | undefined = field?.operations.find(
    (operation) => operation.key === condition.operation?.key
  );

  if (target && field && operation) {
    if (condition.value && Array.isArray(condition.value) && condition.value.length > 0) {
      // If value is an array then check if all the values are included in the new type
      // If yes then use the value, if not then return as empty array
      if (
        condition.value.every((value: DropdownOption) => field.options?.some((option) => option?.key === value?.key))
      ) {
        return condition;
      } else if (operation.key === 'IS_BETWEEN') {
        return condition;
      } else if (field.type === InternalApiAlertFilterExpressionFieldTypeEnum.TIMESTAMP) {
        return { ...condition, value: undefined };
      } else {
        return { ...condition, value: [] };
      }
    } else {
      return condition;
    }
  } else {
    // If not valid then construct a validCondition
    const validCondition: Partial<AlertFiltersConditionExpressionProps> = {
      target: condition.target,
      fieldType: condition.fieldType,
      field: undefined,
      operation: undefined,
      value: undefined,
    };

    // If there is a valid combination of target and field
    // then return that otherwise return valid one from the mapping
    const newField: AlertFilterField | undefined = target?.fields.find(
      (field: AlertFilterField) => field.value.key === condition.field?.key
    );
    if (newField) {
      validCondition.field = newField.value;
      validCondition.fieldType = newField.type;
    } else {
      validCondition.field = target?.fields[0]?.value;
      validCondition.fieldType = target?.fields[0]?.type || condition.fieldType;
    }

    // Based on validCondition target and field check if the operation
    // makes a valid condition. If so then use it, if not return a valid one
    const operations: DropdownOption[] | undefined = target?.fields.find(
      (field) => field.value.key === validCondition.field?.key
    )?.operations;
    if (operations?.some((operation) => operation.key === condition.operation?.key)) {
      validCondition.operation = condition.operation;
    } else {
      validCondition.operation = operations && operations.length > 0 ? operations[0] : undefined;
    }
    return validCondition;
  }
};

export const AlertFilterCondition = ({
  expression,
  alertFiltersObjects,
  removable = false,
  removeCondition,
  onConditionChange,
}: AlertFiltersConditionProps): ReactElement | null => {
  const [condition, setCondition] = useState<Partial<AlertFiltersConditionExpressionProps>>({
    target: expression.target,
    field: expression.field,
    fieldType: expression.fieldType,
    operation: expression.operation,
    value: expression.value,
  });

  const object = alertFiltersObjects.getObject(condition?.target?.key);
  if (!object) {
    return <></>;
  }

  if (!condition || !condition.field) return <></>;
  const field = object.getField(condition.field.key);

  if (!field) {
    return <></>;
  }

  const onChange = (fieldName: string, value: DropdownOption | DropdownOption[] | Dayjs | string | number) => {
    let fieldType: InternalApiAlertFilterExpressionFieldTypeEnum;
    if (Array.isArray(value)) {
      if (value.some((v) => v instanceof dayjs)) {
        fieldType = InternalApiAlertFilterExpressionFieldTypeEnum.TIMESTAMP;
      } else {
        fieldType = InternalApiAlertFilterExpressionFieldTypeEnum.STRING_LIST;
      }
    } else if (value instanceof dayjs) {
      fieldType = InternalApiAlertFilterExpressionFieldTypeEnum.TIMESTAMP;
    } else if (typeof value === 'string') {
      fieldType = InternalApiAlertFilterExpressionFieldTypeEnum.STRING;
    } else if (typeof value === 'number') {
      fieldType = InternalApiAlertFilterExpressionFieldTypeEnum.NUMBER;
    } else {
      const field: AlertFilterField | undefined = object.getField((value as DropdownOption).key);
      fieldType =
        field?.type !== undefined
          ? field.type
          : condition.fieldType !== undefined
            ? condition.fieldType
            : InternalApiAlertFilterExpressionFieldTypeEnum.STRING;
    }

    const updatedCondition = validateCondition(
      {
        ...condition,
        [fieldName]: value,
        fieldType,
      },
      alertFiltersObjects
    );
    setCondition(updatedCondition);
    onConditionChange(updatedCondition);
  };

  return (
    <div className='alert-filter-condition' data-test-id='alert-filter-condition'>
      <SalvDropdown
        ariaLabel='Target'
        name='target'
        value={condition.target}
        options={alertFiltersObjects.getObjectOptions()}
        onChange={(select: DropdownOption[]) => onChange('target', select[0])}
        size={Size.SMALL}
        className='m-r-8'
        clearable={false}
        renderedInBootstrapModal
      />
      <SalvDropdown
        ariaLabel='Field'
        name='field'
        value={condition.field}
        options={object.getFieldOptions()}
        onChange={(select: DropdownOption[]) => onChange('field', select[0])}
        size={Size.SMALL}
        className='m-r-8'
        clearable={false}
        renderedInBootstrapModal
      />
      <SalvDropdown
        ariaLabel='Operation'
        name='operation'
        value={condition.operation}
        options={field.operations}
        onChange={(select: DropdownOption[]) => onChange('operation', select[0])}
        size={Size.SMALL}
        className='m-r-8'
        clearable={false}
        renderedInBootstrapModal
      />
      {renderValueContainer(
        field.type,
        condition?.operation?.key,
        condition.value,
        (value) => onChange('value', value),
        () => {
          if (field.options) {
            return field.options;
          }
          return [];
        }
      )}
      {removable && (
        <Button variant='outline-danger' onClick={removeCondition}>
          Remove
        </Button>
      )}
    </div>
  );
};

export default AlertFilterCondition;
